import { useCallback, useMemo } from "react";
import { useSetEvaluationSelectionMutation, type EvalBidDto } from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { useCreateEditor } from "../../../common/editing/save-coordinator/useCreateEditor";
import { useEditor } from "../../../common/editing/save-coordinator/useEditor";
import { PriceEvaluationView } from "./PriceEvaluationView";

// The bid selection is a whole-array replace on every click. Routing it through the save
// coordinator makes rapid clicks compose against the optimistic state and serialises the
// saves, so two quick clicks can no longer race into "only the latter survives".
interface SelectionState extends Record<string, unknown> {
  selectedBidIds: string[];
}

interface Props {
  eventId: string;
  round: string | undefined;
  bids: EvalBidDto[];
  readOnly: boolean;
  awarded: boolean;
  serverSelectedBidIds: string[];
  onPriceOverride: (
    bidId: string,
    value: number | null,
    comment: string | undefined,
    forDeliverySubmittedAt: string | undefined,
  ) => Promise<void>;
}

export const PriceSelection = (props: Props) => {
  const { eventId, round, serverSelectedBidIds } = props;
  const { trigger } = useApiError();
  const [setSelection] = useSetEvaluationSelectionMutation();

  const entityId = `evaluation-selection:${eventId}:${round ?? "original"}`;
  const initialState = useMemo<SelectionState>(() => ({ selectedBidIds: serverSelectedBidIds }), [serverSelectedBidIds]);
  const save = useCallback(
    (fields: Partial<SelectionState>) =>
      setSelection({ eventId, round, setEvaluationSelectionRequest: { bidIds: fields.selectedBidIds ?? [] } })
        .unwrap()
        .then((r) => ({ selectedBidIds: r.selectedBidIds ?? [] })),
    [eventId, round, setSelection],
  );

  useCreateEditor<SelectionState>(entityId, { initialState, save, onError: (e) => trigger(e) });

  return <PriceSelectionBody {...props} entityId={entityId} />;
};

const PriceSelectionBody = ({ entityId, bids, readOnly, awarded, onPriceOverride }: Props & { entityId: string }) => {
  const { state, updateField } = useEditor<SelectionState>(entityId);
  const selectedBidIds = state.selectedBidIds ?? [];

  const onToggleSelection = (bidId: string) => {
    const next = selectedBidIds.includes(bidId)
      ? selectedBidIds.filter((id) => id !== bidId)
      : [...selectedBidIds, bidId];
    updateField({ selectedBidIds: next });
  };

  return (
    <PriceEvaluationView
      bids={bids}
      readOnly={readOnly}
      awarded={awarded}
      selectedBidIds={selectedBidIds}
      onToggleSelection={onToggleSelection}
      onPriceOverride={onPriceOverride}
    />
  );
};
